import { ComponentPortal } from '@angular/cdk/portal';
import { Component, inject, Injector, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NotificationPanelComponent } from '../notification-panel/notification-panel.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ButtonComponent } from '@orderna/admin-panel/src/app/shared/components/button/button.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { InjectionToken } from '@angular/core';
import { NotificationGatewayService } from '@orderna/admin-panel/src/app/services/notifications-gateway/notification-gateway.service';
import { AuthApiService } from '@orderna/admin-panel/src/app/core/auth/auth-api.service';

export const PANEL_CLOSE_FN = new InjectionToken<() => void>('PANEL_CLOSE_FN');

@Component({
  standalone: true,
  selector: 'app-notification-panel-trigger',
  templateUrl: './notification-panel-trigger.component.html',
  styleUrl: './notification-panel-trigger.component.css',
  imports: [MatIconModule, ButtonComponent],
})
export class NotificationPanelTriggerComponent {
  #overlay = inject(Overlay);
  #breakpoints = inject(BreakpointObserver);
  #notificationGatewayService = inject(NotificationGatewayService);
  #authService = inject(AuthApiService);

  currentUserId = this.#authService.currentUserId;
  unreadCount = this.#notificationGatewayService.unreadCount;
  overlayRef = signal<OverlayRef | null>(null);

  constructor() {
    this.#breakpoints.observe(['(max-width: 767px)']).subscribe((state) => {
      if (this.overlayRef()) {
        //  Close the overlay when the screen transitions between mobile ↔ desktop sizes.
        this.closePanel();
      }
    });

    const currentUserId = this.currentUserId();
    if (currentUserId) {
      this.#notificationGatewayService.subscribeToNotificationsUnreadCount(
        currentUserId
      );
    }
  }

  togglePanel() {
    if (this.overlayRef()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel() {
    const isMobile = this.#breakpoints.isMatched('(max-width: 767px)'); // tailwind md

    let positionStrategy;

    if (isMobile) {
      // Fullscreen mobile overlay
      positionStrategy = this.#overlay
        .position()
        .global()
        .centerHorizontally('0px')
        .centerVertically();
    } else {
      // Normal dropdown
      positionStrategy = this.#overlay
        .position()
        .flexibleConnectedTo(document.querySelector('.trigger-button')!)
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]);
    }

    const overlayRef = this.#overlay.create({
      hasBackdrop: true,
      backdropClass: isMobile ? 'dark-backdrop' : 'transparent-backdrop',
      positionStrategy,
      scrollStrategy: this.#overlay.scrollStrategies.block(),
    });

    const injector = Injector.create({
      providers: [
        { provide: OverlayRef, useValue: overlayRef },
        { provide: PANEL_CLOSE_FN, useValue: () => this.closePanel() },
      ],
    });

    const portal = new ComponentPortal(
      NotificationPanelComponent,
      null,
      injector
    );
    overlayRef.attach(portal);
    overlayRef.backdropClick().subscribe(() => this.closePanel());
    this.overlayRef.set(overlayRef);
  }

  closePanel() {
    const overlayRef = this.overlayRef();
    if (overlayRef) {
      overlayRef.dispose();
      this.overlayRef.set(null);
    }
  }
}
